import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { normalizeText } from "@/lib/text";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, ShieldCheck, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const EVENT_LABELS = {
  "auth.signup": { label: "Inscription", tone: "sage" },
  "auth.login.success": { label: "Connexion réussie", tone: "sage" },
  "auth.login.failed": { label: "Échec de connexion", tone: "terra" },
  "auth.login.lockout": { label: "Verrouillage", tone: "terra" },
  "auth.logout": { label: "Déconnexion", tone: "muted" },
  "auth.google.session": { label: "Connexion Google", tone: "sage" },
  "auth.password.change": { label: "Mot de passe modifié", tone: "sage" },
  "auth.profile.update": { label: "Profil mis à jour", tone: "sage" },
  "response.view": { label: "Consultation réponse", tone: "muted" },
  "response.pdf": { label: "Téléchargement PDF", tone: "sage" },
  "responses.export.xlsx": { label: "Export Excel", tone: "sage" },
  "questionnaire.send": { label: "Envoi questionnaire", tone: "sage" },
  "template.delete": { label: "Suppression modèle", tone: "terra" },
  "patient.view": { label: "Consultation patient", tone: "muted" },
  "study.docs.analyze": { label: "Analyse IA — CR (PII anonymisés)", tone: "sage" },
  "study.docs.append": { label: "Ajout à cohorte — CR (PII anonymisés)", tone: "sage" },
};

function EventBadge({ event }) {
  const cfg = EVENT_LABELS[event] || { label: event, tone: "muted" };
  const classes = {
    sage: "bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6] hover:bg-[#E6F1F5]",
    terra: "bg-[#FFE0DC] text-[#8B2A1E] border border-[#EEC1BB] hover:bg-[#FFE0DC]",
    muted: "bg-[#F4F7FB] text-[#5F758F] hover:bg-[#F4F7FB]",
  }[cfg.tone];
  return <Badge className={classes} data-testid={`audit-badge-${event}`}>{cfg.label}</Badge>;
}

export default function AuditPanel() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [event, setEvent] = useState("all");

  useEffect(() => {
    (async () => {
      const params = {};
      if (event && event !== "all") params.event = event;
      const { data } = await api.get("/audit", { params });
      setItems(data);
    })();
  }, [event]);

  const filtered = items.filter((it) => {
    if (!q) return true;
    const s = normalizeText(q);
    return (
      normalizeText(it.event || "").includes(s) ||
      normalizeText(it.user_email || "").includes(s) ||
      normalizeText(it.resource_id || "").includes(s) ||
      (it.ip || "").includes(q)
    );
  });

  const downloadRegister = async () => {
    try {
      const res = await fetch(`${API}/rgpd/register.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("register failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registre-rgpd-questiomed-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Registre téléchargé");
    } catch (err) {
      log.error("register download failed", err);
      toast.error("Téléchargement impossible");
    }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (event && event !== "all") params.set("event", event);
    try {
      const res = await fetch(`${API}/audit/export.csv?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-questiomed-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Journal exporté");
    } catch (err) {
      log.error("audit export failed", err);
      toast.error("Export impossible");
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-3 bg-[#E6F1F5] border border-[#C7DEE6] rounded-2xl p-4 flex-1">
          <ShieldCheck size={20} weight="duotone" className="text-[#3A8FA5] mt-0.5 shrink-0" />
          <div className="text-sm text-[#1E3A5F]">
            Cette page trace tous les accès et actions sensibles sur les données patients conformément au RGPD. Chaque événement est horodaté et lié à votre adresse IP.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" className="rounded-[10px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white font-semibold" onClick={downloadRegister} data-testid="rgpd-register-btn">
            <FilePdf size={16} className="mr-1.5" /> Registre RGPD
          </Button>
          <Button variant="outline" className="rounded-[10px] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white font-semibold" onClick={exportCsv} data-testid="audit-export-btn">
            <DownloadSimple size={16} className="mr-1.5" /> Exporter CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F]" />
          <Input placeholder="Événement, email, IP…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="audit-search" />
        </div>
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger className="bg-white border-[#E1E9F0] rounded-[10px]" data-testid="audit-event-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les événements</SelectItem>
            {Object.entries(EVENT_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center">
          <p className="text-[#5F758F]">Aucun événement pour ce filtre.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((it, idx) => (
            <div key={`${it.at}-${idx}`} className="bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-4 flex items-center justify-between gap-4 transition-all hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.06)]" data-testid={`audit-row-${idx}`}>
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <EventBadge event={it.event} />
                <div className="min-w-0">
                  <p className="text-sm text-[#1E3A5F]">
                    {it.resource_type && it.resource_id
                      ? <><span className="text-[#5F758F]">{it.resource_type}</span> · <code className="text-xs">{it.resource_id}</code></>
                      : <span className="text-[#5F758F]">—</span>}
                  </p>
                  <p className="text-xs text-[#5F758F] truncate">
                    {it.user_email || "—"} · IP {it.ip || "—"}
                  </p>
                </div>
              </div>
              <p className="text-xs text-[#5F758F] shrink-0 whitespace-nowrap">
                {new Date(it.at).toLocaleString("fr-FR")}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
